import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiLoader, 
  FiEye,
  FiSave,
  FiX,
  FiSearch
} from "react-icons/fi";

const FAQsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  
  // Form states
  const [newFaqs, setNewFaqs] = useState([{ question: "", answer: "" }]);
  const [editingFaqs, setEditingFaqs] = useState({ id: null, faqs: [] });
  
  // Loading states
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const API_URL = "https://api.dreamtoday.in/api/admin";

  // Fetch all FAQs
  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/allfaqs`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        setFaqs(response.data.faqs || []);
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.error("Failed to fetch FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  // Filter FAQs based on search
  const filteredFaqs = faqs.filter(faqDoc => 
    searchTerm === "" || 
    faqDoc.faqs.some(faq => 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
    ) ||
    faqDoc._id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle create FAQs
  const handleCreateFAQs = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const validFaqs = newFaqs.filter(faq => 
        faq.question.trim() !== "" && faq.answer.trim() !== ""
      );
      
      if (validFaqs.length === 0) {
        toast.error("At least one FAQ with question and answer is required");
        setCreating(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/addfaqs`,
        { faqs: validFaqs },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("FAQs created successfully!");
        setShowCreateModal(false);
        setNewFaqs([{ question: "", answer: "" }]);
        fetchFAQs();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create FAQs");
    } finally {
      setCreating(false);
    }
  };

  // Handle update FAQs
  const handleUpdateFAQs = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const validFaqs = editingFaqs.faqs.filter(faq => 
        faq.question.trim() !== "" && faq.answer.trim() !== ""
      );
      
      if (validFaqs.length === 0) {
        toast.error("At least one FAQ with question and answer is required");
        setUpdating(false);
        return;
      }

      const response = await axios.put(
        `${API_URL}/updatefaqs/${editingFaqs.id}`,
        { faqs: validFaqs },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("FAQs updated successfully!");
        setShowEditModal(false);
        setEditingFaqs({ id: null, faqs: [] });
        fetchFAQs();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update FAQs");
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete FAQs
  const handleDeleteFAQs = async () => {
    if (!selectedFaq) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/deletefaqs/${selectedFaq._id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("FAQs deleted successfully!");
        setShowDeleteModal(false);
        setSelectedFaq(null);
        fetchFAQs();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete FAQs");
    } finally {
      setDeleting(false);
    }
  };

  // Form handlers
  const addFaqField = () => setNewFaqs([...newFaqs, { question: "", answer: "" }]);
  const removeFaqField = (index) => {
    if (newFaqs.length > 1) {
      setNewFaqs(newFaqs.filter((_, i) => i !== index));
    }
  };
  const updateFaqField = (index, field, value) => {
    const updated = [...newFaqs];
    updated[index][field] = value;
    setNewFaqs(updated);
  };

  const addEditFaqField = () => {
    setEditingFaqs({
      ...editingFaqs,
      faqs: [...editingFaqs.faqs, { question: "", answer: "" }]
    });
  };
  const removeEditFaqField = (index) => {
    if (editingFaqs.faqs.length > 1) {
      setEditingFaqs({
        ...editingFaqs,
        faqs: editingFaqs.faqs.filter((_, i) => i !== index)
      });
    }
  };
  const updateEditFaqField = (index, field, value) => {
    const updated = [...editingFaqs.faqs];
    updated[index][field] = value;
    setEditingFaqs({ ...editingFaqs, faqs: updated });
  };

  const formatDate = (date) => new Date(date).toLocaleDateString();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FiLoader className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">FAQs Management</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <FiPlus /> Add FAQs
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Table */}
        {filteredFaqs.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500">No FAQs found</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">FAQ Set</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Total</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Created</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredFaqs.map((faqDoc, idx) => (
                  <tr key={faqDoc._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium">
                        {faqDoc.faqs[0]?.question.substring(0, 50)}...
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {faqDoc._id.slice(-6)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                        {faqDoc.faqs.length} FAQs
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(faqDoc.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedFaq(faqDoc);
                            setShowViewModal(true);
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="View"
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() => {
                            setEditingFaqs({
                              id: faqDoc._id,
                              faqs: faqDoc.faqs.map(f => ({ question: f.question, answer: f.answer }))
                            });
                            setShowEditModal(true);
                          }}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedFaq(faqDoc);
                            setShowDeleteModal(true);
                          }}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Create FAQs</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleCreateFAQs} className="space-y-4">
                  {newFaqs.map((faq, index) => (
                    <div key={index} className="border p-4 rounded-lg relative">
                      {newFaqs.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFaqField(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <FiX size={14} />
                        </button>
                      )}
                      <input
                        type="text"
                        placeholder="Question"
                        value={faq.question}
                        onChange={(e) => updateFaqField(index, "question", e.target.value)}
                        className="w-full mb-2 p-2 border rounded"
                        required
                      />
                      <textarea
                        placeholder="Answer"
                        value={faq.answer}
                        onChange={(e) => updateFaqField(index, "answer", e.target.value)}
                        rows="2"
                        className="w-full p-2 border rounded"
                        required
                      />
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addFaqField}
                    className="w-full py-2 border-2 border-dashed rounded-lg text-gray-600 hover:text-blue-600"
                  >
                    + Add Another FAQ
                  </button>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creating}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {creating ? "Creating..." : "Create"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Edit FAQs</h2>
                  <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleUpdateFAQs} className="space-y-4">
                  {editingFaqs.faqs.map((faq, index) => (
                    <div key={index} className="border p-4 rounded-lg relative">
                      {editingFaqs.faqs.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEditFaqField(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <FiX size={14} />
                        </button>
                      )}
                      <input
                        type="text"
                        placeholder="Question"
                        value={faq.question}
                        onChange={(e) => updateEditFaqField(index, "question", e.target.value)}
                        className="w-full mb-2 p-2 border rounded"
                        required
                      />
                      <textarea
                        placeholder="Answer"
                        value={faq.answer}
                        onChange={(e) => updateEditFaqField(index, "answer", e.target.value)}
                        rows="2"
                        className="w-full p-2 border rounded"
                        required
                      />
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addEditFaqField}
                    className="w-full py-2 border-2 border-dashed rounded-lg text-gray-600 hover:text-blue-600"
                  >
                    + Add Another FAQ
                  </button>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {updating ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedFaq && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">FAQ Details</h2>
                  <button onClick={() => setShowViewModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">FAQ ID</p>
                    <p className="font-mono">{selectedFaq._id}</p>
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Created At</p>
                    <p>{new Date(selectedFaq.createdAt).toLocaleString()}</p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-semibold">FAQs ({selectedFaq.faqs.length})</h3>
                    {selectedFaq.faqs.map((faq, idx) => (
                      <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2">
                        <p className="font-medium">Q{idx + 1}: {faq.question}</p>
                        <p className="text-gray-600 mt-1">A: {faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setEditingFaqs({
                        id: selectedFaq._id,
                        faqs: selectedFaq.faqs.map(f => ({ question: f.question, answer: f.answer }))
                      });
                      setShowEditModal(true);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && selectedFaq && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-bold mb-2">Delete FAQs</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this FAQ set?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteFAQs}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQsManagement;